// Migas (breadcrumbs) de la barra superior: de la RUTA a la lista de tramos.
//
// POR QUÉ salen de la ruta y no de cada página: son once páginas y trece
// herramientas de proyecto, y si cada una armara sus migas bastaría con que una
// se olvidara para que el "¿dónde estoy?" dejara de ser confiable justo ahí. La
// ruta es la única fuente que ya dice la verdad en todas; lo que la ruta no sabe
// (el NOMBRE del proyecto, del tablero, de la conversación) llega como contexto.
//
// Es un módulo PURO a propósito: las pruebas lo recorren ruta por ruta sin montar
// router, ni react-query, ni topbar.

export interface Miga {
  etiqueta: string
  /** Ruta a la que lleva el tramo. El último (donde estás) no lleva. */
  to?: string
}

/** Lo que la ruta sola no sabe. Todo opcional: sin dato se usa un nombre genérico. */
export interface ContextoMigas {
  proyecto?: string
  tablero?: string
  /** Nombre del tramo final cuando la página lo sabe y la ruta no (p. ej. la conversación). */
  hoja?: string
}

// El nombre de cada herramienta TAL COMO LA LLAMA su tarjeta del home del
// proyecto (OverviewTab) y el camino de etapas: si la miga dijera otra palabra, la
// persona tendría que traducir entre dos nombres del mismo lugar.
export const HERRAMIENTAS: Record<string, string> = {
  brief: 'Brief',
  debrief: 'Debrief',
  'nota-creativa': 'Nota de creatividad', // así la nombra el camino de etapas del home
  propuesta: 'Propuesta IA',
  visual: 'Visual',
  presentacion: 'Presentación',
  tareas: 'Tareas',
  mensajes: 'Message board',
  chat: 'Chat',
  timetable: 'Timetable',
  notas: 'Notas',
  recursos: 'Recursos',
  archivos: 'Archivos',
}

// Secciones de primer nivel. Mismos nombres que el dock.
const SECCIONES: Record<string, string> = {
  '/mis-tareas': 'Mis tareas',
  '/panorama': 'Panorama',
  '/chat': 'Chat',
  '/celulas': 'Células',
  '/directorio': 'Directorio',
  '/papelera': 'Papelera',
  '/admin': 'Administración',
  '/perfil': 'Mi perfil',
  '/enlazar': 'Enlazar Mac',
  '/ayuda': 'Ayuda',
  // El resolvedor de ligas viejas: casi nunca se ve (redirige solo), salvo cuando
  // la liga no tiene equivalente en camp.
  '/bc': 'Liga de Basecamp',
}

export const INICIO: Miga = { etiqueta: 'Inicio', to: '/' }

/**
 * Tramos de la ruta, de Inicio a donde estás. El último nunca lleva `to`.
 * `search` importa solo en el message board: `?post=` abre un mensaje DENTRO de
 * la herramienta y la persona lo vive como un nivel más.
 */
export function migasDeRuta(pathname: string, search = '', ctx: ContextoMigas = {}): Miga[] {
  const [raiz = '', pid = '', tab = '', boardId = ''] = pathname.split('/').filter(Boolean).map(decodificar)
  const out: Miga[] = [INICIO]

  if (raiz === 'p' && pid) {
    const base = `/p/${pid}`
    out.push({ etiqueta: ctx.proyecto?.trim() || 'Proyecto', to: base })
    // /p/:pid/t/:boardId es un tablero concreto: vive bajo Tareas.
    if (tab === 't' && boardId) {
      out.push({ etiqueta: HERRAMIENTAS.tareas ?? 'Tareas', to: `${base}/tareas` })
      out.push({ etiqueta: ctx.tablero?.trim() || 'Tablero', to: pathname })
    } else if (tab) {
      out.push({ etiqueta: HERRAMIENTAS[tab] ?? capitalizar(tab), to: `${base}/${tab}` })
      if (tab === 'mensajes' && new URLSearchParams(search).get('post')) {
        out.push({ etiqueta: ctx.hoja?.trim() || 'Mensaje', to: `${base}/${tab}${search}` })
      }
    }
  } else if (raiz === 'chat' && pid) {
    out.push({ etiqueta: 'Chat', to: '/chat' })
    out.push({ etiqueta: ctx.hoja?.trim() || 'Conversación', to: pathname })
  } else if (raiz) {
    out.push({ etiqueta: SECCIONES[`/${raiz}`] ?? capitalizar(raiz), to: `/${raiz}` })
  }

  // El último tramo es "aquí": sin enlace. Inicio en la raíz también.
  const ultimo = out.pop() ?? INICIO
  out.push({ etiqueta: ultimo.etiqueta })
  return out
}

/**
 * A dónde lleva "atrás" cuando NO hay historial dentro de la app (se entró por una
 * liga de correo, de notificación o de chat): al tramo anterior de las migas. Es
 * la misma respuesta que la persona ve escrita en la barra, así que el botón nunca
 * la sorprende. En Inicio no hay padre.
 */
export function padreLogico(pathname: string, search = '', ctx: ContextoMigas = {}): Miga | null {
  const migas = migasDeRuta(pathname, search, ctx)
  if (migas.length < 2) return null
  const padre = migas[migas.length - 2]
  return padre?.to ? padre : null
}

/**
 * ¿Hay a dónde regresar SIN salir de camp? React Router guarda en
 * `history.state.idx` la posición dentro de la sesión de la app: 0 es la entrada
 * con la que se abrió (una liga, un marcador, la app recién lanzada). Mayor que
 * cero garantiza que la entrada anterior también es de camp, y entonces "atrás"
 * es el atrás de verdad —con su scroll y sus filtros—, no una reconstrucción.
 */
export function hayHistorialInterno(estado: unknown = history.state): boolean {
  const idx = (estado as { idx?: unknown } | null)?.idx
  return typeof idx === 'number' && idx > 0
}

function decodificar(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

function capitalizar(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ')
}
